<h2 mat-dialog-title mat-dialog-title>{{ dialogData.mode }} User</h2>
@if (showApiMessage) {
<ctx-alert [alertLevel]="apiMessage.alertLevel">{{
  apiMessage.text
}}</ctx-alert>
}
<form [formGroup]="formGroup" (ngSubmit)="_submit()">
  <mat-dialog-content>
    @if (project !== 'admin-portal') {
    <ng-container *ngTemplateOutlet="details"></ng-container>
    } @if (project !== 'customer-portal' && project !== 'reseller-portal') {
    <ctx-tab-group #tab>
      <ctx-tab label="Details" [stepControl]="detailsTab" icon="DETAILS">
        <ng-container *ngTemplateOutlet="details"></ng-container>
      </ctx-tab>
      @if (project === 'admin-portal') {
      <ctx-tab label="Metadata" [stepControl]="metadataTab" icon="METADATA">
        <ctx-metadata-form
          [dialogData]="dialogData"
          [hintText]="METADATA_HINT_TEXT"
          [parentForm]="this"
        ></ctx-metadata-form>
      </ctx-tab>
      }
    </ctx-tab-group>
    }
  </mat-dialog-content>
  <mat-dialog-actions>
    <button
      ctx-button--secondary
      matDialogClose
      [disabled]="formStates.disabled"
    >
      Close
    </button>
    <button
      ctx-button--primary
      [disabled]="formStates.disabled"
      [loading]="formStates.submit"
      type="submit"
    >
      {{ dialogData.mode }}
    </button>
  </mat-dialog-actions>
  <ng-template #details>
    <section class="flex flex-col gap-4">
      <!-- First Name -->
      <mat-form-field>
        <mat-label>First Name</mat-label>
        <input
          autocomplete="off"
          type="text"
          formControlName="firstName"
          matInput
          required
        />
        <mat-error>{{ getControlError(formGroup, 'firstName') }}</mat-error>
      </mat-form-field>

      <!-- Last Name -->
      <mat-form-field>
        <mat-label>Last Name</mat-label>
        <input
          autocomplete="off"
          type="text"
          formControlName="lastName"
          matInput
          required
        />
        <mat-error>{{ getControlError(formGroup, 'lastName') }}</mat-error>
      </mat-form-field>

      <!-- Email -->
      <mat-form-field>
        <mat-label>Email</mat-label>
        <input
          autocomplete="off"
          type="email"
          formControlName="email"
          matInput
          required
        />
        <mat-error>{{ getControlError(formGroup, 'email') }}</mat-error>
      </mat-form-field>

      <!-- Password -->
      @if (dialogData.mode === 'Create') {
      <mat-form-field>
        <mat-label>Password</mat-label>
        <input
          [type]="isPasswordVisible ? 'text' : 'password'"
          formControlName="password"
          matInput
          required
        />
        <span class="flex flex-row items-center gap-1" matSuffix>
          <button
            ctx-button--secondary
            color="accent"
            type="button"
            size="sm"
            [actionIcon]="getVisibilityButtonIcon(isPasswordVisible)"
            (click)="isPasswordVisible = !isPasswordVisible"
          ></button>
          <button
            ctx-button--secondary
            color="accent"
            size="sm"
            actionIcon="GENERATE_PASSWORD"
            type="button"
            matTooltip="Generate Password"
            (click)="handlePasswordCheckbox()"
          ></button>
        </span>
        <mat-error>{{ getControlError(formGroup, 'password') }}</mat-error>
      </mat-form-field>
      }

      <!-- Roles -->
      @if (project !== 'customer-portal') {
      <mat-form-field>
        <mat-label>Role</mat-label>
        <mat-select
          formControlName="role"
          (selectionChange)="handleRoleChange($event)"
        >
          @for (role of userRoles; track role) {
          <mat-option class="ph-no-capture" [value]="role">
            {{ role }}
          </mat-option>
          }
        </mat-select>
        <mat-error>{{ getControlError(formGroup, 'role') }}</mat-error>
        @if (formGroup.get('role')?.value === null) {
        <mat-hint>Role of the user.</mat-hint>
        } @if (formGroup.get('role')?.value === 'user') {
        <mat-hint>The least permissive role for a user.</mat-hint>
        } @if (formGroup.get('role')?.value === 'organization-admin') {
        <mat-hint
          >The organization-admin role will allow this user to create Users and
          User Groups in the linked Organization.</mat-hint
        >
        } @if (formGroup.get('role')?.value === 'reseller-admin') {
        <mat-hint
          >The reseller-admin role will allow this user to create Licenses,
          Users and Organizations for the linked Reseller.</mat-hint
        >
        }
      </mat-form-field>
      }

      <!-- Password generation method selection-->
      @if (dialogData.mode === 'Create') {
      <section></section>
      }

      <!-- Company -->
      @if ( formGroup.get('role')?.value === 'user' && project !==
      'customer-portal' ) {
      <mat-form-field>
        <mat-label>Company</mat-label>
        <input
          autocomplete="off"
          type="text"
          formControlName="company"
          matInput
        />
        <mat-error>{{ getControlError(formGroup, 'company') }}</mat-error>
        <mat-hint
          >Use "Company" instead of "Organization" to store only the company
          name.</mat-hint
        >
      </mat-form-field>
      }

      <!-- Organization -->
      @if ( (formGroup.get('role')?.value === 'user' ||
      formGroup.get('role')?.value === 'organization-admin') && project !==
      'customer-portal' ) {
      <mat-form-field>
        <mat-label>Organization</mat-label>
        <ctx-customer-search
          formControlName="organizationId"
          [type]="'organization'"
          [required]="formGroup.get('role')?.value === 'organization-admin'"
          [read]="this.organizationService.read"
          [list]="organizationService.list"
        ></ctx-customer-search>
        <mat-error>{{
          getControlError(formGroup, 'organizationId')
        }}</mat-error>
        <mat-hint>Type to find organizations </mat-hint>
      </mat-form-field>
      }

      <!-- Reseller -->
      @if ( formGroup.get('role')?.value === 'reseller-admin' && project ===
      'admin-portal' ) {
      <mat-form-field>
        <mat-label>Reseller</mat-label>
        <ctx-customer-search
          formControlName="resellerId"
          [type]="'reseller'"
          [required]="formGroup.get('role')?.value === 'reseller-admin'"
          [read]="this.resellerService.read"
          [list]="this.resellerService.list"
        ></ctx-customer-search>
        <mat-error>{{ getControlError(formGroup, 'resellerId') }}</mat-error>
        <mat-hint>Type to find resellers </mat-hint>
      </mat-form-field>
      }

      <!-- Tags -->
      @if (project === 'admin-portal') {
      <ctx-tag-input
        [control]="formGroup.get('tags')"
        [allowedTags]="allowedTagsOnUsers"
      ></ctx-tag-input>

      } @if (project === 'customer-portal') {
      <mat-form-field>
        <mat-label>User Groups</mat-label>
        <mat-select formControlName="userGroupIds" required multiple>
          @for (userGroup of userGroups; track userGroup) {
          <mat-option [value]="userGroup.id">
            {{ userGroup.name }}
          </mat-option>
          }
        </mat-select>
      </mat-form-field>
      }

      <!-- Allow Customer Portal Access -->
      @if (dialogData.mode === 'Create' || project === 'admin-portal') {
      <section>
        <mat-checkbox formControlName="allowCustomerPortalAccess"
          >Allow Customer Portal Access</mat-checkbox
        >
        <label
          >If checked, user is allowed to access the customer portal with their
          credentials.</label
        >
      </section>
      }
    </section>
  </ng-template>
</form>
